import React, { useState, useEffect } from 'react';
import { Banner } from '@pwc/appkit-react/lib/Banner';
import { Button } from '@pwc/appkit-react/lib/Button';
import { formatDistance } from 'date-fns';

const SessionExpirationBanner = _ref => {
  let {
    refreshSession,
    timeDistance
  } = _ref;
  const warningMessage = /*#__PURE__*/React.createElement(React.Fragment, null, "Your session will expire in ", timeDistance, " due to inactivity", /*#__PURE__*/React.createElement(Button, {
    className: "banner-button",
    onClick: refreshSession
  }, "Refresh"));
  return /*#__PURE__*/React.createElement("div", {
    className: "session-warning-banner"
  }, /*#__PURE__*/React.createElement(Banner, {
    status: "warning",
    content: warningMessage,
    closeable: true
  }));
};

const SessionExpirationOverlay = _ref => {
  let {
    refreshSession,
    timeDistance
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    className: "session-warning-overlay"
  }, /*#__PURE__*/React.createElement("div", {
    className: "overlay-info-text"
  }, "Session expired ", timeDistance, " ago"), /*#__PURE__*/React.createElement("div", {
    className: "overlay-button-wrapper"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "lg",
    onClick: refreshSession
  }, "REFRESH")));
};

const UNKNOWN_TIME_DISTANCE = 'an unknown time';
const TimeStatus = {
  ACTIVE: 'Active',
  ENDING: 'Ending',
  EXPIRED: 'Expired'
};
const getTimeStatus = _ref => {
  let {
    expirationDate,
    currentDate,
    threshold
  } = _ref;
  const isTimeExpired = currentDate >= expirationDate;
  const isThresholdReached = expirationDate - currentDate <= threshold;
  if (isTimeExpired) {
    return TimeStatus.EXPIRED;
  }
  if (isThresholdReached) {
    return TimeStatus.ENDING;
  }
  return TimeStatus.ACTIVE;
};
const useExpirationTime = _ref2 => {
  let {
    extractExpirationDate,
    refreshInterval,
    threshold,
    refreshSession
  } = _ref2;
  const [timeStatus, setTimeStatus] = useState(TimeStatus.ACTIVE);
  const [timeDistance, setTimeDistance] = useState('');
  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const expirationDate = extractExpirationDate();
        const currentDate = Date.now();
        const timeStatus = getTimeStatus({
          expirationDate,
          currentDate,
          threshold
        });
        const timeDistance = formatDistance(expirationDate, currentDate, {
          includeSeconds: true
        });
        setTimeStatus(timeStatus);
        setTimeDistance(timeDistance);
      } catch (error) {
        const result = await refreshSession();
        if (!result || !result.ok) {
          setTimeStatus(TimeStatus.EXPIRED);
          setTimeDistance(UNKNOWN_TIME_DISTANCE);
        }
      }
    }, refreshInterval);
    return () => clearInterval(intervalId);
  }, []);
  return {
    timeStatus,
    timeDistance
  };
};

const ONE_SECOND = 1000;
const FIVE_MINUTES = 5 * 60 * 1000;
const sessionGuards = {
  [TimeStatus.ENDING]: SessionExpirationBanner,
  [TimeStatus.EXPIRED]: SessionExpirationOverlay
};
const SessionExpirationGuard = _ref => {
  let {
    refreshSession,
    extractExpirationDate,
    refreshInterval,
    threshold
  } = _ref;
  const {
    timeStatus,
    timeDistance
  } = useExpirationTime({
    refreshSession,
    extractExpirationDate,
    refreshInterval,
    threshold
  });
  const SessionGuard = sessionGuards[timeStatus];
  if (!SessionGuard) {
    return null;
  }
  return /*#__PURE__*/React.createElement(SessionGuard, {
    refreshSession: refreshSession,
    timeDistance: timeDistance
  });
};
SessionExpirationGuard.defaultProps = {
  threshold: FIVE_MINUTES,
  refreshInterval: ONE_SECOND
};

export { SessionExpirationGuard as default, useExpirationTime };
